import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {ArrowForwardOutlined, SearchOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import Message from '../../../shared/message/Message.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import useFetch from '../../../../hooks/useFetch.js';
import {URL_INSCRICOES_ALUNO} from '../../../../constants/index.js';
import {regrasDoPrograma} from '../../components/inscricao/inscricaoAdministrativaPayload.js';

const ID_TITULO = 'dialog-nova-inscricao-titulo';

const digitos = valor => String(valor ?? '').replace(/\D/g, '');

const useStyles = makeStyles(theme => ({
    root: {
        width: 800
    },
    contentRoot: {
        overflowY: 'initial'
    },
    colunaBotao: {
        display: 'flex',
        alignItems: 'flex-end'
    },
    botaoCarregar: {
        height: 56
    },
    alunoCarregado: {
        marginTop: theme.spacing(1)
    }
}));

/*
 * Primeiro passo da criação de inscrição: escolher o processo de seleção.
 * No veterano, pede também o código do aluno e só continua depois de carregar
 * os dados dele (GET /inscricoes/alunos/{codigo}). Continuar devolve
 * {idProcessoSeletivo, codigoAluno, voAluno}; Fechar fica a cargo da página.
 */
export default function DialogNovaInscricao({open, onClose, processos = [], valorInicial, onContinuar}) {
    const classes = useStyles();
    const fetch = useFetch();
    const [idProcessoSeletivo, setIdProcessoSeletivo] = useState('');
    const [codigoAluno, setCodigoAluno] = useState('');
    const [voAluno, setVoAluno] = useState(null);
    const [erroCodigo, setErroCodigo] = useState('');

    const processo = processos.find(item => String(item.id) === String(idProcessoSeletivo)) || null;
    const veterano = !!processo && regrasDoPrograma(processo.programa?.id, processo).exibirCodigoAluno;
    const podeContinuar = !!processo && (!veterano || !!voAluno);

    /* Re-popula a cada abertura (padrão §1.7). */
    const onEntering = () => {
        setIdProcessoSeletivo(valorInicial?.idProcessoSeletivo ? String(valorInicial.idProcessoSeletivo) : '');
        setCodigoAluno(valorInicial?.codigoAluno ? String(valorInicial.codigoAluno) : '');
        setVoAluno(null);
        setErroCodigo('');
    };

    const aoMudarProcesso = evento => {
        setIdProcessoSeletivo(evento.target.value);
        setVoAluno(null);
        setErroCodigo('');
    };

    const aoMudarCodigo = evento => {
        setCodigoAluno(digitos(evento.target.value).slice(0, 6));
        setVoAluno(null);
        setErroCodigo('');
    };

    const carregarAluno = () => {
        if (!codigoAluno) {
            setErroCodigo('É obrigatório');
            return;
        }
        fetch(URL_INSCRICOES_ALUNO(codigoAluno), {idProcessoSeletivo}, vo => {
            if (vo) setVoAluno(vo);
        });
    };

    const continuar = () => {
        if (!podeContinuar) return;
        onContinuar({idProcessoSeletivo, codigoAluno: veterano ? codigoAluno : '', voAluno: veterano ? voAluno : null});
    };

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering}} scroll='body'
                aria-labelledby={ID_TITULO} classes={{paper: classes.root}}>
            <DialogTitle id={ID_TITULO}>Nova Inscrição</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <LabelValue label='Processo de seleção *'>
                            <FormControl variant='outlined' fullWidth>
                                <Select value={idProcessoSeletivo} onChange={aoMudarProcesso} displayEmpty>
                                    <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                    {processos.map(item => (
                                        <MenuItem key={item.id} value={String(item.id)}>{item.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {!idProcessoSeletivo && <FormHelperText>Escolha o processo para preencher a inscrição.</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    {veterano && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='Código do aluno *'>
                                    <FormControl variant='outlined' fullWidth error={!!erroCodigo}>
                                        <OutlinedInput
                                            value={codigoAluno}
                                            onChange={aoMudarCodigo}
                                            onKeyPress={evento => evento.key === 'Enter' && carregarAluno()}
                                            inputProps={{maxLength: 6, inputMode: 'numeric'}}/>
                                        {!!erroCodigo && <FormHelperText error>{erroCodigo}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.colunaBotao}>
                                <OutlinedButton startIcon={<SearchOutlined/>} className={classes.botaoCarregar} onClick={carregarAluno}>
                                    Carregar dados
                                </OutlinedButton>
                            </Grid>
                            <Grid item xs={12}>
                                {voAluno ? (
                                    <Message type='success' variant='standard'>
                                        {`Dados carregados: ${voAluno.pessoa?.nome || 'aluno'}.`}
                                    </Message>
                                ) : (
                                    <Message type='info' variant='standard'>
                                        Veterano: informe o código do aluno e clique em Carregar dados para continuar.
                                    </Message>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
                <ContainedButton startIcon={<ArrowForwardOutlined/>} disabled={!podeContinuar} onClick={continuar}>
                    Continuar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogNovaInscricao.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    processos: PropTypes.array,
    valorInicial: PropTypes.object,
    onContinuar: PropTypes.func.isRequired
};
